import { useState } from "react";
import { Button, Input, Label, Select, cn } from "@clevis/shared-ui";
import { useEntitlements } from "../hooks/useEntitlements";
import { call } from "../lib/callFunction";
import { useJobSettings, type JobTypePay } from "../hooks/useJobSettings";
import { JOB_PAGE_SECTIONS } from "@clevis/shared-types";
import { DRY_RUN_LABEL, workflowFor, type DryRunOutcome, type JobWorkflow } from "@clevis/shared-types";

// Job type + title, the way a call-taker works: tap "Service" and the title
// becomes "Service - " ready for the rest ("Service - door won't close").
// Leave it and the dangling " - " is dropped on save (finalizeJobTitle).

const prefix = (type: string) => `${type} - `;

/** Title with the type's "Type - " prefix removed ("" when it was only the type). */
function withoutPrefix(title: string, type: string): string {
  if (!type) return title;
  if (title.startsWith(prefix(type))) return title.slice(prefix(type).length);
  const t = title.trim();
  return t === type || t === `${type} -` ? "" : title;
}

/** The title to save: tidy spaces, drop a trailing "-", fall back to the type. */
export function finalizeJobTitle(title: string, jobType: string): string {
  const t = title.replace(/\s+/g, " ").trim().replace(/\s*-$/, "").trim();
  return (t || jobType).slice(0, 120);
}

export function JobTypeTitle({
  types,
  jobType,
  title,
  onChange,
}: {
  types: string[];
  jobType: string;
  title: string;
  onChange: (next: { jobType: string; title: string }) => void;
}) {
  // A job keeps its type even if it was later removed from the list.
  const shown = jobType && !types.includes(jobType) ? [...types, jobType] : types;

  function pick(type: string) {
    const rest = withoutPrefix(title, jobType);
    if (type === jobType) onChange({ jobType: "", title: rest });
    else onChange({ jobType: type, title: prefix(type) + rest.trimStart() });
  }

  return (
    <>
      <div className="col-span-2">
        <Label>Type</Label>
        <div className="flex flex-wrap gap-1.5">
          {shown.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={t === jobType}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm",
                t === jobType ? "border-ink bg-ink text-white" : "border-steel/30 bg-white text-ink hover:border-ink",
              )}
              onClick={() => pick(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="col-span-2">
        <Label>Title</Label>
        <Input
          value={title}
          placeholder={jobType ? "" : "e.g. Door won't close"}
          onChange={(e) => onChange({ jobType, title: e.target.value })}
        />
      </div>
    </>
  );
}

// Admin: edit the list the call-taker taps (Jobs → Job settings), and how
// each type pays the tech by default: hourly, or piece rate (+ a flat amount
// per job of that type).
export function JobTypesSetting({ tenantId }: { tenantId: string; types?: string[] }) {
  const settings = useJobSettings(tenantId);
  const [draft, setDraft] = useState<string[] | null>(null);
  const [draftPay, setDraftPay] = useState<JobTypePay | null>(null);
  const [draftSplit, setDraftSplit] = useState<"even" | "each" | null>(null);
  const [draftExtras, setDraftExtras] = useState<"auto" | "approve" | null>(null);
  const [draftFlows, setDraftFlows] = useState<Record<string, string[]> | null>(null);
  const [flowOpen, setFlowOpen] = useState<string | null>(null);
  const [draftWorkflows, setDraftWorkflows] = useState<Record<string, Partial<JobWorkflow>> | null>(null);
  const [stepsOpen, setStepsOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const list = draft ?? settings.types;
  const pay = draftPay ?? settings.pay;
  const split = draftSplit ?? settings.pieceSplit;
  const extras = draftExtras ?? settings.pieceExtras;
  const { hasFeature } = useEntitlements();
  const payOn = hasFeature("pieceRate");
  const flowsOn = hasFeature("jobFlows");
  const flows = draftFlows ?? settings.flows;
  const workflows = draftWorkflows ?? settings.workflows;
  const dirty = draft !== null || draftPay !== null || draftSplit !== null || draftExtras !== null || draftFlows !== null || draftWorkflows !== null;
  const toggleHidden = (t: string, id: string) => {
    const hidden = new Set(flows[t] ?? []);
    if (hidden.has(id)) hidden.delete(id);
    else hidden.add(id);
    setDraftFlows({ ...flows, [t]: [...hidden] });
  };

  function add() {
    const t = adding.replace(/\s+/g, " ").trim();
    if (!t || list.some((x) => x.toLowerCase() === t.toLowerCase())) return setAdding("");
    setDraft([...list, t]);
    setAdding("");
  }
  function move(i: number, by: -1 | 1) {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x!);
    setDraft(next);
  }
  const setPay = (t: string, patch: Partial<JobTypePay[string]>) =>
    setDraftPay({ ...pay, [t]: { mode: pay[t]?.mode ?? "hourly", flatCents: pay[t]?.flatCents ?? 0, ...patch } });
  async function save() {
    setBusy(true);
    setNote("");
    try {
      await call("setJobTypes", { tenantId, types: list, pay, pieceSplit: split, pieceExtras: extras, flows, workflows });
      setDraft(null);
      setDraftPay(null);
      setDraftSplit(null);
      setDraftExtras(null);
      setDraftFlows(null);
      setDraftWorkflows(null);
      setNote("Saved.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-ink">Job types</div>
      <p className="mb-2 text-[11px] text-steel">
        What the person taking the call taps on a new job (the type starts the title), and how that kind of call pays the
        tech: by the hour (time clock) or piece rate. A piece-rate job also earns per part installed (set on each pricebook
        item) and per project item (e.g. per lot), shown on the weekly piece-pay report.
      </p>
      <div className="space-y-1">
        {list.map((t, i) => (
          <div key={t} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="min-w-28 flex-1 text-ink">{t}</span>
            {payOn ? (
            <Select
              aria-label={`${t} pays`}
              className="w-auto px-2 py-0.5 text-xs"
              value={pay[t]?.mode ?? "hourly"}
              onChange={(e) => setPay(t, { mode: e.target.value as "hourly" | "piece" })}
            >
              <option value="hourly">Hourly</option>
              <option value="piece">Piece rate</option>
            </Select>
            ) : null}
            {payOn && (pay[t]?.mode ?? "hourly") === "piece" ? (
              <label className="flex items-center gap-1 text-[11px] text-steel">
                flat $
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-20 rounded border border-steel/30 px-1 py-0.5 text-xs text-ink"
                  value={((pay[t]?.flatCents ?? 0) / 100).toString()}
                  onChange={(e) => setPay(t, { flatCents: Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)) })}
                />
                per job
              </label>
            ) : null}
            <button type="button" className="text-xs text-steel underline hover:text-ink" onClick={() => setStepsOpen(stepsOpen === t ? null : t)}>
              steps
            </button>
            {flowsOn ? (
              <button type="button" className="text-xs text-steel underline hover:text-ink" onClick={() => setFlowOpen(flowOpen === t ? null : t)}>
                {(flows[t] ?? []).length ? `techs see ${JOB_PAGE_SECTIONS.length - (flows[t] ?? []).length}/${JOB_PAGE_SECTIONS.length}` : "techs see all"}
              </button>
            ) : null}
            <button type="button" className="text-xs text-steel hover:text-ink disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${t} up`}>
              ↑
            </button>
            <button type="button" className="text-xs text-steel hover:text-ink disabled:opacity-30" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${t} down`}>
              ↓
            </button>
            <button type="button" className="text-xs text-steel hover:text-danger disabled:opacity-30" disabled={list.length === 1} onClick={() => setDraft(list.filter((x) => x !== t))}>
              Remove
            </button>
            {stepsOpen === t
              ? (() => {
                  const w = workflowFor(t, workflows);
                  const set = (p: Partial<JobWorkflow>) => setDraftWorkflows({ ...workflows, [t]: { ...w, ...p } });
                  const tick = (key: keyof Omit<JobWorkflow, "dryRun">, label: string) => (
                    <label className="flex items-center gap-1.5 text-xs text-ink">
                      <input type="checkbox" checked={w[key]} onChange={(e) => set({ [key]: e.target.checked })} />
                      {label}
                    </label>
                  );
                  return (
                    <div className="w-full rounded-lg bg-cream/60 p-2">
                      <p className="mb-1 text-[11px] text-steel">On a {t} job the tech presses, in order: {w.onTheWay ? "On my way → " : ""}I'm here → Finish job{w.partial ? " / Partial complete" : ""}{w.dryRun !== "off" ? " / Dry run" : ""}.</p>
                      <div className="grid gap-x-3 gap-y-1 sm:grid-cols-2">
                        {tick("onTheWay", "“On my way” step (texts the customer)")}
                        {tick("partial", "“Partial complete” (opens a return visit for dispatch)")}
                        {tick("arrivePhotos", "Arriving asks for photos")}
                        {tick("arriveNotes", "Arriving asks for a note")}
                        {tick("completeInvoice", "Finishing offers the invoice")}
                        {tick("approveToStart", "Ask the customer to OK the visit first (in their portal; shown on the job, never a gate)")}
                        <label className="flex items-center gap-1.5 text-xs text-ink sm:col-span-2">
                          <span className="shrink-0">Dry run:</span>
                          <Select className="w-auto py-0.5 text-xs" value={w.dryRun} onChange={(e) => set({ dryRun: e.target.value as DryRunOutcome })}>
                            {(Object.keys(DRY_RUN_LABEL) as DryRunOutcome[]).map((k) => (
                              <option key={k} value={k}>
                                {DRY_RUN_LABEL[k]}
                              </option>
                            ))}
                          </Select>
                        </label>
                      </div>
                    </div>
                  );
                })()
              : null}
            {flowsOn && flowOpen === t ? (
              <div className="w-full rounded-lg bg-cream/60 p-2">
                <p className="mb-1 text-[11px] text-steel">
                  On a {t} job, field techs see (the office always sees everything; unticked parts are blocked on the server too):
                </p>
                <div className="grid gap-x-3 gap-y-0.5 sm:grid-cols-2">
                  {JOB_PAGE_SECTIONS.map((sec) => (
                    <label key={sec.id} className="flex items-center gap-1.5 text-xs text-ink">
                      <input type="checkbox" checked={!(flows[t] ?? []).includes(sec.id)} onChange={() => toggleHidden(t, sec.id)} />
                      {sec.label}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          className="py-1 text-sm"
          placeholder="Add a type, e.g. Inspection"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={add}>
          Add
        </Button>
      </div>
      <label className={cn("mt-2 flex flex-wrap items-center gap-1 text-[11px] text-steel", !payOn && "hidden")}>
        Two techs on a piece-rate job:
        <Select
          className="w-auto px-2 py-0.5 text-[11px]"
          value={split}
          onChange={(e) => setDraftSplit(e.target.value as "even" | "each")}
        >
          <option value="even">split the pieces evenly</option>
          <option value="each">each earns them in full</option>
        </Select>
      </label>
      <label className={cn("mt-1 flex flex-wrap items-center gap-1 text-[11px] text-steel", !payOn && "hidden")}>
        Parts a tech logs beyond the plan:
        <Select className="w-auto px-2 py-0.5 text-[11px]" value={extras} onChange={(e) => setDraftExtras(e.target.value as "auto" | "approve")}>
          <option value="auto">pay their pieces right away</option>
          <option value="approve">pay only after a manager's OK (Timesheets)</option>
        </Select>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Button type="button" className="px-3 py-1 text-xs" busy={busy} disabled={!dirty} onClick={() => void save()}>
          Save job types
        </Button>
        {dirty ? (
          <Button
            type="button"
            variant="ghost"
            className="px-3 py-1 text-xs"
            onClick={() => {
              setDraft(null);
              setDraftPay(null);
              setDraftSplit(null);
              setDraftExtras(null);
              setDraftFlows(null);
            }}
          >
            Undo changes
          </Button>
        ) : null}
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
    </div>
  );
}
